import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { profilesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Heart, ArrowLeft, ArrowRight, Sparkles, Star, Loader2, Lock, Globe, Users, MapPin } from 'lucide-react';

const STEPS = [
  { id: 'basics', title: 'Who will you honour?', subtitle: 'The quiet hero who shaped your life' },
  { id: 'type', title: 'Choose tribute type', subtitle: 'Are they here with us today?' },
  { id: 'headline', title: 'Their impact on you', subtitle: 'What did they give you that changed everything?' },
  { id: 'privacy', title: 'Privacy settings', subtitle: 'Who can see this tribute?' }
];

const RELATIONSHIPS = [
  'Mother', 'Father', 'Parents', 'Grandmother', 'Grandfather', 
  'Sister', 'Brother', 'Daughter', 'Son',
  'Wife', 'Husband', 'Partner', 
  'Friend', 'Best Friend',
  'Mentor', 'Teacher', 'Coach',
  'Colleague', 'Boss',
  'Other'
];

export default function CreateTributePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    person_name: '',
    relationship: '',
    profile_type: 'living',
    headline: '',
    privacy_setting: 'private',
    location_city: '',
    location_country: '',
    date_of_birth: '',
    date_of_passing: ''
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const canProceed = () => {
    switch (step) {
      case 0: return formData.person_name.trim() && formData.relationship;
      case 1: return formData.profile_type;
      case 2: return formData.headline.trim();
      case 3: return formData.privacy_setting;
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await profilesApi.create(formData);
      navigate(`/edit/${data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create tribute');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-purple-100/50 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/3 right-1/3 w-[400px] h-[400px] bg-amber-100/50 rounded-full blur-[100px]" />
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#1a1a2e]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#1a1a2e]/50 hover:text-[#1a1a2e] transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm">Back to dashboard</span>
          </Link>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7c3aed] to-[#db2777] flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" strokeWidth={2} fill="currentColor" />
            </div>
            <span className="font-bold text-[#1a1a2e]">iExtol</span>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 relative z-10">
        <div className="flex gap-2">
          {STEPS.map((s, i) => (
            <div 
              key={s.id}
              className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                i <= step ? 'bg-gradient-to-r from-[#7c3aed] to-[#db2777]' : 'bg-[#1a1a2e]/10'
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-[#1a1a2e]/40 mt-3">
          Step {step + 1} of {STEPS.length}
        </p>
      </div>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12 relative z-10">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1a1a2e] tracking-tight mb-2">
            {STEPS[step].title}
          </h1>
          <p className="text-[#1a1a2e]/50">{STEPS[step].subtitle}</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="create-error">
            {error}
          </div>
        )}

        {/* Step 0: Basics */}
        {step === 0 && (
          <Card className="border-[#1a1a2e]/10 bg-white shadow-lg">
            <CardContent className="p-8 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="person_name" className="text-[#1a1a2e]/70">Their name</Label>
                <Input
                  id="person_name"
                  value={formData.person_name}
                  onChange={(e) => updateField('person_name', e.target.value)}
                  placeholder="Enter their name"
                  className="border-[#1a1a2e]/10 focus:border-[#7c3aed] rounded-xl text-lg py-6"
                  data-testid="person-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="relationship" className="text-[#1a1a2e]/70">Your relationship</Label>
                <Select value={formData.relationship} onValueChange={(v) => updateField('relationship', v)}>
                  <SelectTrigger className="border-[#1a1a2e]/10 focus:border-[#7c3aed] rounded-xl text-lg py-6" data-testid="relationship-select">
                    <SelectValue placeholder="Select your relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {RELATIONSHIPS.map(rel => (
                      <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Tribute Type */}
        {step === 1 && (
          <>
          <RadioGroup 
            value={formData.profile_type} 
            onValueChange={(v) => updateField('profile_type', v)}
            className="grid md:grid-cols-2 gap-6"
          >
            <Label htmlFor="living" className="cursor-pointer">
              <Card className={`border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                formData.profile_type === 'living' 
                  ? 'border-emerald-400 bg-emerald-50' 
                  : 'border-[#1a1a2e]/10 bg-white'
              }`}>
                <CardContent className="p-8 text-center">
                  <RadioGroupItem value="living" id="living" className="sr-only" />
                  <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg ${
                    formData.profile_type === 'living' ? 'bg-emerald-500 shadow-emerald-500/30' : 'bg-[#1a1a2e]/5'
                  }`}>
                    <Sparkles className={`w-8 h-8 ${formData.profile_type === 'living' ? 'text-white' : 'text-[#1a1a2e]/30'}`} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1a1a2e] mb-2">Living Tribute</h3>
                  <p className="text-emerald-600 text-sm font-semibold mb-2">Tell them while they can hear it</p>
                  <p className="text-sm text-[#1a1a2e]/50">
                    Let them know their impact on your life while they're here.
                  </p>
                </CardContent>
              </Card>
            </Label>

            <Label htmlFor="legacy" className="cursor-pointer">
              <Card className={`border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                formData.profile_type === 'legacy' 
                  ? 'border-violet-400 bg-violet-50' 
                  : 'border-[#1a1a2e]/10 bg-white'
              }`}>
                <CardContent className="p-8 text-center">
                  <RadioGroupItem value="legacy" id="legacy" className="sr-only" />
                  <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg ${
                    formData.profile_type === 'legacy' ? 'bg-violet-500 shadow-violet-500/30' : 'bg-[#1a1a2e]/5'
                  }`}>
                    <Star className={`w-8 h-8 ${formData.profile_type === 'legacy' ? 'text-white' : 'text-[#1a1a2e]/30'}`} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1a1a2e] mb-2">Legacy Tribute</h3>
                  <p className="text-violet-600 text-sm font-semibold mb-2">Keep their light alive</p>
                  <p className="text-sm text-[#1a1a2e]/50">
                    Preserve the memory and impact of those who have passed.
                  </p>
                </CardContent>
              </Card>
            </Label>
          </RadioGroup>

          {/* Date fields */}
          <Card className="border-[#1a1a2e]/10 bg-white shadow-lg mt-6">
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-[#1a1a2e]/70">Date of birth (optional)</Label>
                <Input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => updateField('date_of_birth', e.target.value)}
                  className="border-[#1a1a2e]/10 focus:border-[#7c3aed] rounded-xl w-48"
                  data-testid="date-of-birth-input"
                />
              </div>
              {formData.profile_type === 'legacy' && (
                <div className="space-y-2">
                  <Label className="text-[#1a1a2e]/70">Date of passing</Label>
                  <Input
                    type="date"
                    value={formData.date_of_passing}
                    onChange={(e) => updateField('date_of_passing', e.target.value)}
                    className="border-[#1a1a2e]/10 focus:border-[#7c3aed] rounded-xl w-48"
                    data-testid="date-of-passing-input"
                  />
                </div>
              )}
            </CardContent>
          </Card>
          </>
        )}

        {/* Step 2: Headline */}
        {step === 2 && (
          <Card className="border-[#1a1a2e]/10 bg-white shadow-lg">
            <CardContent className="p-8 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="headline" className="text-[#1a1a2e]/70">How did {formData.person_name || 'this person'} impact your life?</Label>
                <Textarea
                  id="headline"
                  value={formData.headline}
                  onChange={(e) => updateField('headline', e.target.value)}
                  placeholder="E.g., 'She worked three jobs so I could go to college' or 'He believed in me when I didn't believe in myself'"
                  className="border-[#1a1a2e]/10 focus:border-[#7c3aed] rounded-xl text-lg min-h-[140px] resize-none"
                  data-testid="headline-input"
                />
                <p className="text-xs text-[#1a1a2e]/40">
                  Capture their quiet heroism—the sacrifice, the belief, the impact
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Privacy */}
        {step === 3 && (
          <RadioGroup 
            value={formData.privacy_setting} 
            onValueChange={(v) => updateField('privacy_setting', v)}
            className="space-y-4"
          >
            {[
              { value: 'private', icon: Lock, title: 'Private', desc: 'Only you can view this tribute', color: 'amber' },
              { value: 'invite-only', icon: Users, title: 'Invite Only', desc: 'Share with specific people via link', color: 'violet' },
              { value: 'public', icon: Globe, title: 'Public', desc: 'Anyone can view and be inspired', color: 'emerald' }
            ].map(option => (
              <Label key={option.value} htmlFor={option.value} className="cursor-pointer">
                <Card className={`border-2 transition-all duration-300 ${
                  formData.privacy_setting === option.value 
                    ? `border-${option.color}-400 bg-${option.color}-50` 
                    : 'border-[#1a1a2e]/10 bg-white hover:border-[#1a1a2e]/20'
                }`}>
                  <CardContent className="p-5 flex items-center gap-4">
                    <RadioGroupItem value={option.value} id={option.value} className="sr-only" />
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      formData.privacy_setting === option.value ? `bg-${option.color}-500 shadow-lg shadow-${option.color}-500/30` : 'bg-[#1a1a2e]/5'
                    }`}>
                      <option.icon className={`w-5 h-5 ${
                        formData.privacy_setting === option.value ? 'text-white' : 'text-[#1a1a2e]/30'
                      }`} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a1a2e]">{option.title}</h3>
                      <p className="text-sm text-[#1a1a2e]/50">{option.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              </Label>
            ))}
          </RadioGroup>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-10">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={step === 0}
            className="text-[#1a1a2e]/50 hover:text-[#1a1a2e] hover:bg-[#1a1a2e]/5 rounded-full"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={!canProceed() || loading}
            className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] hover:from-[#6d28d9] hover:to-[#be185d] text-white rounded-full px-8 font-medium shadow-lg shadow-purple-500/20 transition-all duration-300"
            data-testid="next-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : step === STEPS.length - 1 ? (
              <>
                Create tribute
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}

